/**
 * Short, non-technical copy for end users. Sentence case throughout.
 * Strips vendor names and common HTTP/SDK noise from anything shown in UI.
 */
const CANONICAL = new Set([
  "Your Session Has Expired. Please Login Again.",
  "Service is temporarily unavailable. Please try again.",
  "Check your internet connection and try again.",
  "Microphone access is required for calls.",
  "Something went wrong. Please try again.",
  "Account setup is incomplete. Please sign in again.",
  "Calling could not be set up. Please sign in again.",
  "Call setup is incomplete. Please sign in again.",
  "Could not save activity details. Your call can continue.",
  "Could not load call history. Please try again.",
  "Please enter a valid phone number.",
  "Call service is not ready. Please try again.",
  "Could not accept the call. Please try again.",
  "Call did not connect. Please try again.",
  "Call ended.",
  "Calling could not start. Please try again.",
  "Could not verify call readiness. Please try again.",
  "Could not start call service. Please try again.",
  "You are online for calls.",
  "You appear offline. Calls may not work.",
  "Live transcription could not start.",
  "Live transcription started.",
  "Add participant is not available yet.",
]);

export function normalizeUserFacingMessage(input) {
  const raw = String(input ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return "";

  if (CANONICAL.has(raw)) return raw;

  const lower = raw.toLowerCase();

  if (/exotel/.test(lower)) {
    if (/expired|invalid\s*authorization|unauthorized|401|not\s*valid|token/.test(lower)) {
      return "Your Session Has Expired. Please Login Again.";
    }
    return "Calling could not be set up. Please sign in again.";
  }

  if (/401|403|unauthorized|invalid\s*authorization|token.*expired|expired.*token|not\s*valid/.test(lower)) {
    return "Your Session Has Expired. Please Login Again.";
  }

  if (/500|internal\s*server\s*error|server\s*error/.test(lower)) {
    return "Service is temporarily unavailable. Please try again.";
  }

  if (/network\s*error|failed\s*to\s*fetch|offline|timeout|timed\s*out|load\s*failed|net::/.test(lower)) {
    return "Check your internet connection and try again.";
  }

  if (
    /cannot\s*destructure\s*property\s*['"]?appid['"]?|userData\s*must\s*be\s*configured|typeerror|referenceerror|syntaxerror/.test(
      lower
    )
  ) {
    return "Account setup is incomplete. Please sign in again.";
  }

  if (/microphone|permission|notallowederror|media_permission/.test(lower)) {
    return "Microphone access is required for calls.";
  }

  if (/\bunknown\b/.test(lower)) {
    return "Something went wrong. Please try again.";
  }

  if (/api\s*key|authentication\s*failed|invalid\s*credentials/.test(lower)) {
    return "Your Session Has Expired. Please Login Again.";
  }

  if (/exception|stack|trace|\.ts:|\.tsx:|\.js:\d+|at\s+\w+\s*\(/.test(lower)) {
    return "Something went wrong. Please try again.";
  }

  if (/^https?:\/\//i.test(raw)) {
    return "Something went wrong. Please try again.";
  }

  const cleaned = raw
    .replace(/^make_call_api_error\s*[:;-]?\s*/i, "")
    .replace(/^error\s*[:;-]?\s*/i, "")
    .trim();

  if (!cleaned || cleaned.length > 120) {
    return "Something went wrong. Please try again.";
  }

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function userFacingApiMessage(status, payload, fallback) {
  const rawMessage =
    (payload && (payload.message || payload.error || payload.details || payload.detail)) || "";

  if (typeof rawMessage === "string" && rawMessage.trim()) {
    return normalizeUserFacingMessage(rawMessage.trim());
  }

  if (status === 401 || status === 403) return "Your Session Has Expired. Please Login Again.";
  if (status === 404) return "Service is temporarily unavailable. Please try again.";
  if (status >= 500) return "Service is temporarily unavailable. Please try again.";

  return normalizeUserFacingMessage(fallback || "Something went wrong. Please try again.");
}
